import { Button, OutlinedSelect, TextOutlinedInput } from '@octano/global-ui';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { useContinuousEducationSectionsFiltersFormLoaderData } from '../hooks/ContinuousEducationSectionsFiltersFormLoader';
import { useContinuousEducationSectionsLoader } from '../hooks/ContinuousEducationSectionsLoader';
import { ContinuousEducationSectionFiltersFields } from '../types';

const DEFAULTS: ContinuousEducationSectionFiltersFields = {
  name: null,
  code: null,
  school: null,
  courseName: null,
  courseCode: null,
  schedule: null,
  campus: null,
  teacher: null,
  modality: null,
};

const SectionsSearch = () => {
  const { t } = useTranslation(TranslationsKeys.CONTINUOUS_EDUCATION_SECTIONS);

  const { updateQuery, query } = useContinuousEducationSectionsLoader();
  const {
    schedules,
    schools,
    teachers,
    campuses,
    modalities,
  } = useContinuousEducationSectionsFiltersFormLoaderData();

  const {
    control,
    reset,
    handleSubmit,
  } = useForm<ContinuousEducationSectionFiltersFields>({
    defaultValues: DEFAULTS,
  });

  const handleSearch = useCallback(
    (values: ContinuousEducationSectionFiltersFields) => {
      updateQuery({
        page: 1,
        items_per_page: query.items_per_page,
        code: values.code ?? undefined,
        courseName: values.courseName ?? undefined,
        courseCode: values.courseCode ?? undefined,
        school: values.school?.value ? String(values.school?.value) : undefined,
        modality: values.modality?.value
          ? String(values.modality?.value)
          : undefined,
        schedule: values.schedule?.value
          ? String(values.schedule?.value)
          : undefined,
        campus: values.campus?.value ? String(values.campus?.value) : undefined,
        teacher: values.teacher?.value
          ? String(values.teacher?.value)
          : undefined,
      });
    },
    [updateQuery, query],
  );

  const handleClean = useCallback(() => {
    reset(DEFAULTS);
    updateQuery({
      page: 1,
      items_per_page: query.items_per_page,
    });
  }, [reset, updateQuery, query]);

  return (
    <Row className="mb-4">
      <Col xs={12} sm={6} md={4} lg={3}>
        <TextOutlinedInput
          control={control}
          name="code"
          label={t('search.code.label')}
          placeholder={t('search.code.placeholder')}
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={schools}
          name="school"
          label={t('search.school.label')}
          placeholder={t('search.school.placeholder')}
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <TextOutlinedInput
          control={control}
          name="courseName"
          label={t('search.courseName.label')}
          placeholder={t('search.courseName.placeholder')}
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <TextOutlinedInput
          control={control}
          name="courseCode"
          label={t('search.courseCode.label')}
          placeholder={t('search.courseCode.placeholder')}
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={schedules}
          name="schedule"
          label={t('search.schedule.label')}
          placeholder={t('search.schedule.placeholder')}
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={campuses}
          name="campus"
          label={t('search.campus.label')}
          placeholder={t('search.campus.placeholder')}
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={teachers}
          name="teacher"
          label={t('search.teacher.label')}
          placeholder={t('search.teacher.placeholder')}
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <OutlinedSelect
          control={control}
          options={modalities}
          name="modality"
          label={t('search.modality.label')}
          placeholder={t('search.modality.placeholder')}
        />
      </Col>
      <Col xs={12}>
        <div className="w-100 d-flex justify-content-end align-items-center">
          <div
            className="w-100 d-flex justify-content-end align-items-center"
            style={{ gap: 5, maxWidth: 300 }}
          >
            <div className="flex-fill">
              <Button
                type="button"
                text={t('common:actions.search')}
                size="md"
                fullwidth
                onClick={handleSubmit(handleSearch)}
              />
            </div>
            <div className="flex-fill">
              <Button
                type="button"
                text={t('common:actions.clean')}
                size="md"
                fullwidth
                onClick={handleClean}
                outlined
              />
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
};
export default SectionsSearch;
